import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/auth/AuthModal';
import FundingCard from '../components/funding/FundingCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api';

const FAQItem = ({ question, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-lg font-medium text-gray-900 pr-4">
          {question}
        </span>
        <span className="text-2xl text-gray-400 flex-shrink-0">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="pb-5 pr-12">
          <p className="text-gray-600 leading-relaxed">
            {children}
          </p>
        </div>
      )}
    </div>
  );
};

const FundCollectionCard = ({ collection }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/funding?fund_collection=${collection.id}&group_by=fund_collection`);
  };

  const handleBlogClick = (e) => {
    e.stopPropagation();
    navigate(`/blog/${collection.blog_post.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden border border-gray-100 p-6 flex flex-col h-full"
    >
      {/* Title */}
      <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-3">
        {collection.name}
      </h3>

      {/* Bottom section */}
      <div className="mt-auto pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          {/* Count - smaller and more subtle */}
          <span className="text-sm text-gray-600">
            <span className="font-semibold" style={{ color: '#615DEC' }}>
              {collection.scheme_count}
            </span>
            {' '}{collection.scheme_count === 1 ? 'action' : 'actions'}
          </span>

          {/* Blog link - as text link instead of button */}
          {collection.has_blog_post && (
            <button
              onClick={handleBlogClick}
              className="text-sm font-medium hover:underline flex items-center gap-1"
              style={{ color: '#615DEC' }}
            >
              Learn more
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sampleFundings, setSampleFundings] = useState([]);
  const [fundCollections, setFundCollections] = useState([]);
  const [currentCollectionIndex, setCurrentCollectionIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const navigate = useNavigate();

  const funders = [
    'defra.png',
    'Essex-Suffolk-Water-logo.png',
    'Northumbrian-Water-logo.png',
    'forestry-commission-transparent.png',
    'SevernTrentWater.jpg',
    'South_Staffordshire_Water_logo.svg.png',
    'UKRI_IUK-Logo_Horiz-RGB.png',
  ];

  useEffect(() => {
    const fetchSampleFundings = async () => {
      try {
        // Fetch total count separately (page_size=1 to minimise payload)
        const countEndpoint = `/funding-schemes/?page_size=1`;

        const [featuredResponse, countResponse] = await Promise.all([
          api.get('/featured-schemes/'),
          api.get(countEndpoint),
        ]);

        setSampleFundings(featuredResponse.data);
        setTotalCount(countResponse.data.count || 0);
      } catch (error) {
        console.error('Failed to load sample fundings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSampleFundings();
  }, []);

  useEffect(() => {
    const fetchFundCollections = async () => {
      try {
        const response = await api.get('/fund-collections/');
        const sortedCollections = (response.data.results || []).sort((a, b) => 
          b.scheme_count - a.scheme_count
        );
        setFundCollections(sortedCollections || []);
      } catch (error) {
        console.error('Failed to load fund collections:', error);
      } finally {
        setCollectionsLoading(false);
      }
    };

    fetchFundCollections();
  }, []);

  const handlePreviousCollections = () => {
    setCurrentCollectionIndex((prev) => Math.max(0, prev - 3));
  };

  const handleNextCollections = () => {
    setCurrentCollectionIndex((prev) => {
      const totalPages = Math.ceil(fundCollections.length / 3);
      const nextIndex = prev + 3;
      const maxIndex = (totalPages - 1) * 3;
      return Math.min(maxIndex, nextIndex);
    });
  };

  // Calculate visible collections and navigation state
  const visibleCollections = fundCollections.slice(currentCollectionIndex, currentCollectionIndex + 3);
  const canGoBack = currentCollectionIndex > 0;
  const canGoForward = currentCollectionIndex + 3 < fundCollections.length;

  const handleCloseModal = () => {
    setShowAuthModal(false);
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
    >
      {/* Hero Section */}
      <div className="text-center pt-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <h1
            className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-8"
            style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
          >
            Find funding opportunities for your farm
          </h1>

          <p
            className="text-xl md:text-2xl text-gray-600 mb-8"
            style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
          >
            Independent directory of UK public and private agricultural funding
          </p>

          {/* Subtext with funding count */}
          <p
            className="text-lg md:text-xl text-gray-600 mb-12"
            style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
          >
            {loading ? (
              "Loading opportunities..."
            ) : (
              <Link
                to="/funding"
                className="font-medium hover:underline"
                style={{ color: '#615DEC' }}
              >
                {`View ${totalCount.toLocaleString()} funding opportunities`}
              </Link>
            )}
          </p>
        </div>


        {/* Search Button */}
        <div className="max-w-sm mx-auto mb-12">
          <button
            onClick={(e) => {
              e.preventDefault();
              if (!isAuthenticated) {
                setShowAuthModal(true);
                return;
              }
              navigate('/funding');
            }}
            className="w-auto px-8 py-6 font-semibold text-white text-2xl rounded-md transition-all shadow-lg hover:shadow-xl hover:opacity-90"
            style={{ backgroundColor: '#615DEC' }}
          >
            Search
          </button>
        </div>

        {/* Sample Funding Cards */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2
              className="text-3xl font-bold text-gray-900"
              style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
            >
              Featured Opportunities
            </h2>
            <Link
              to="/funding"
              className="text-sm font-medium hover:underline"
              style={{ color: '#615DEC', fontFamily: "'Poppins', system-ui, sans-serif" }}
            >
              View all →
            </Link>
          </div>

          {loading ? (
            <LoadingSpinner size="large" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sampleFundings.length > 0 ? (
                sampleFundings.slice(0, 6).map((funding) => (
                  <FundingCard key={funding.id} funding={funding} />
                ))
              ) : (
                <div className="col-span-3 text-center py-8">
                  <p>No funding opportunities found</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fund Collections Carousel */}
        {fundCollections.length > 0 && (
          <div className="mb-12 mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2
                className="text-3xl font-bold text-gray-900"
                style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
              >
                Explore Funding Collections
              </h2>
            </div>

            {collectionsLoading ? (
              <LoadingSpinner size="large" />
            ) : (
              <div className="relative">
                {/* Carousel Controls */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={handlePreviousCollections}
                    disabled={!canGoBack}
                    aria-label="Previous collections"
                    className={`p-2 rounded-full transition-all ${
                      canGoBack
                        ? 'bg-white shadow-md hover:shadow-lg'
                        : 'bg-gray-100 cursor-not-allowed opacity-50'
                    }`}
                    style={{ color: canGoBack ? '#615DEC' : '#999' }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Cards */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {visibleCollections.map((collection) => (
                      <FundCollectionCard key={collection.id} collection={collection} />
                    ))}
                  </div>

                  <button
                    onClick={handleNextCollections}
                    disabled={!canGoForward}
                    aria-label="Next collections"
                    className={`p-2 rounded-full transition-all ${
                      canGoForward
                        ? 'bg-white shadow-md hover:shadow-lg'
                        : 'bg-gray-100 cursor-not-allowed opacity-50'
                    }`}
                    style={{ color: canGoForward ? '#615DEC' : '#999' }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Dots indicator */}
                <div className="flex justify-center mt-6 gap-2">
                  {Array.from({ length: Math.ceil(fundCollections.length / 3) }).map((_, index) => {
                    const isActive = Math.floor(currentCollectionIndex / 3) === index;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => setCurrentCollectionIndex(index * 3)}
                        className={`h-2 rounded-full transition-all ${
                          isActive ? 'w-8' : 'w-2 bg-gray-300'
                        }`}
                        style={{
                          backgroundColor: isActive ? '#615DEC' : undefined
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Funders Section */}
        <div className="mb-16 mt-20">
          <h2
            className="text-3xl font-bold text-gray-900 text-left mb-8"
            style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
          >
            Featured Funders
          </h2>
          
          <div className="overflow-hidden relative">
            <div className="flex animate-scroll">
              {/* Duplicate the logos twice for seamless loop */}
              {[...funders, ...funders].map((logo, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 mx-8"
                >
                  <img
                    src={`/funders/${logo}`}
                    alt={logo.replace('.png', '').replace(/-/g, ' ')}
                    className="h-16 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {!isAuthenticated && (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 border border-gray-200 mt-12">
            <h3
              className="text-2xl font-bold text-gray-900 mb-3"
              style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
            >
              Ready to find the right funding?
            </h3>
            <p className="text-gray-600 mb-6">
              Create your free account to unlock full details and save opportunities
            </p>
            <Link
              to="/signup"
              className="inline-block px-8 py-3 rounded-md font-medium text-white hover:opacity-90 transition-opacity text-lg"
              style={{ backgroundColor: '#615DEC' }}
            >
              Get Started Free
            </Link>
          </div>
        )}
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto mt-20 mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left side - Title and CTA */}
          <div className="lg:col-span-1">
            <h2
              className="text-3xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
            >
              Your questions, answered
            </h2>
            <Link
              to="/faq"
              className="mt-6 px-6 py-3 border-2 border-gray-900 text-gray-900 font-medium rounded-md hover:bg-gray-900 hover:text-white transition-colors block text-center"
            >
              View all FAQs
            </Link>
          </div>

          {/* Right side - Questions */}
          <div className="lg:col-span-2 space-y-1">
            <FAQItem question="Who is Oko Ag?">
              Oko Ag is a technology company dedicated to helping UK farmers navigate funding opportunities. We aggregate and organise information about public and private farm funding opportunities to make it easier to find the right funding for their farm business.
            </FAQItem>

            <FAQItem question="Is Oko linked to DEFRA or other funders?">
              No, Oko Ag operates independently. We are not affiliated with DEFRA or any funding bodies. We compile information about funding opportunities to help farmers make informed decisions about which to pursue.
            </FAQItem>

            <FAQItem question="How do we use your data?">
              We take your privacy seriously. Your data is used solely to provide you with relevant funding opportunities and improve our service. We never sell your information to third parties. You can view our full privacy policy for detailed information about how we collect, store, and use your data.
            </FAQItem>

            <FAQItem question="How much does it cost?">
              Creating an account and browsing funding opportunities is completely free. We believe all farmers should have access to information about available funding without barriers. Premium features may be added in the future.
            </FAQItem>

            <FAQItem question="What areas do you cover?">
              Right now, we are mainly focused on England, but we are constantly adding new opportunities. Our aim is to cover all public and private funding opportunities across the UK.
            </FAQItem>

            <FAQItem question="How do I get my funding opportunities listed?">
              We are building a comprehensive database of funding opportunities for UK farmers. Email us at hello@oko.ag to have your opportunity included.
            </FAQItem>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          handleCloseModal();
        }}
        action="view full funding details"
      />
    </div>
  );
};

export default HomePage;